/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
// Extend From
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
// Ember Imports
import { get, set, action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
// External imports
import moment from 'moment';
// Internal imports
import DataType from '@linc-technologies/linced-common/enums/data-type';
import ExpulsionStage from 'linced-app/enums/expulsion-stage';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import Staff from '@linc-technologies/linced-common/enums/roles/staff';
import {
  filterValue,
  getNumericYearLevelFilter,
} from '@linc-technologies/linced-common/utils/filtering';
import { IncidentOutcome } from 'linced-app/enums/behaviours';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';
import { rollbackRecord } from '@linc-technologies/linced-common/utils/record-rollback';

export default class ExpulsionsController extends Controller {
  // Enums
  ExpulsionStage = ExpulsionStage;

  // Services
  @service
  countdownButton;

  @service
  session;

  @service
  fieldOptions;

  @service
  permissions;

  @service
  toaster;

  @service
  roles;

  // Attrs
  // queryParams provides a binding to the routes query params.
  queryParams = [
    'dateFrom',
    'dateTo',
    'termId',
    'highlightedExpulsionId',
    'highlightedSuspension',
    'openDialog',
  ];

  // State
  // Set the initial selected tab
  @tracked selectedTab = ExpulsionStage.PENDING_EXPULSION;

  // dateFrom provides the stateful binding to a query param.
  @tracked
  dateFrom = `${moment().year()}-01-01`;

  // dateTo provides the stateful binding to a query param.
  @tracked
  dateTo = `${moment().year()}-12-31`;

  // termId provides the ability to select a term for date filtering.
  @tracked
  termId = '';

  @tracked
  filters = [];
  @tracked
  highlightedExpulsionId = '';
  openDialog = false;
  @tracked
  selectedExpulsion = null;
  @tracked
  selectedExpulsionEdit = null;
  @tracked
  stage = ExpulsionStage.PENDING_EXPULSION;

  @tracked
  postMode;

  @tracked
  selectedExpulsionDelete;

  @tracked
  selectedExpulsionPosts;

  @computed('session.{roles.[],scopes.[]}')
  get canManageExpulsions() {
    return (
      this.roles.hasAny(Staff.PRINCIPAL, Staff.PRINCIPAL_S_DELEGATE) ||
      this.permissions.meet(Scopes.LINCED_ADMIN)
    );
  }

  @computed('session.activeSchool', 'fieldOptions.yearLevelOptions.[]')
  get yearLevelOptions() {
    let options = findBy(
      this.get('fieldOptions.yearLevelOptions'),
      'schoolId',
      this.get('session.activeSchool'),
    );
    if (isEmpty(options) || isEmpty(options.options)) {
      return [];
    }
    return options.options;
  }

  @computed(
    'model.expulsions.@each.stage',
    'filters.@each.{field,value}',
    'selectedTab',
  )
  get filteredExpulsions() {
    if (isEmpty(this.model.expulsions)) {
      return [];
    }
    let filteredExpulsions = this.model.expulsions;

    filteredExpulsions = filteredExpulsions.filter((row) => {
      return row.stage === this.selectedTab;
    });

    if (isEmpty(this.filters)) {
      // No filters - just return the data.
      return filteredExpulsions;
    }

    // Determine datatypes for any specified filters.
    let filters = this.filters.map((filter) => {
      let dataType = DataType.STRING;
      return {
        key: filter.field,
        dataType,
        value: filter.value,
      };
    });

    // Determine numeric filter for year level, if applicable.
    let yearLevelFilterValue = getNumericYearLevelFilter(
      filters.find((item) => item.key === 'yearLevel'),
      this.yearLevelOptions,
    );

    // Apply all filters to each row in one hit, to avoid iterating over a
    // (potentially) large dataset multiple times.
    return filteredExpulsions.filter((row) => {
      return filters.every((filter) => {
        // Field-specific filter processing.
        switch (filter.key) {
          case 'yearLevel':
            return filterValue(
              row.get('learner.numericCurrentYearLevel') ?? '',
              DataType.INTEGER,
              yearLevelFilterValue,
            );
        }

        // Generic filter processing.
        return filterValue(
          get(row, filter.key) ?? '',
          filter.dataType,
          filter.value,
        );
      });
    });
  }

  onActivate() {
    // Make sure no dialogs are open
    this.closeDialog();

    let tab = ExpulsionStage.PENDING_EXPULSION;

    // Select the right tab based on highlighted expulsion
    if (!isEmpty(this.highlightedExpulsionId)) {
      const highlightedExpulsion = this.model.expulsions.find(
        (item) => item.id === this.highlightedExpulsionId,
      );
      if (!isEmpty(highlightedExpulsion)) {
        tab = highlightedExpulsion.get('stage');
      }
    }

    this.selectedTab = tab;
  }

  /**
   * onChangeDateFrom changes the dateFrom field and removes the currently
   * set term as the date has changed, therefore not explicitly the currently
   * set term.
   *
   * @param {string} newDate - The new dateFrom to change the value to.
   */
  @action
  onChangeDateFrom(newDate) {
    this.dateFrom = newDate;
    this.termId = null;
  }

  /**
   * onChangeDateTo changes the dateTo field and removes the currently set
   * term as the date has changed.
   *
   * @param {string} newDate - The new dateFrom to change the value to.
   */
  @action
  onChangeDateTo(newDate) {
    this.dateTo = newDate;
    this.termId = null;
  }

  /**
   * setSelectedTermId sets the query params, which in turn will refresh the
   * model.
   *
   * @param {Object} term - A term record.
   */
  @action
  setSelectedTermId(term) {
    this.dateFrom = term.start;
    this.dateTo = term.end;
    this.termId = term.id;
  }

  @action
  filter(field, value) {
    let filter = this.filters.find((item) => item.field === field);
    if (isEmpty(filter)) {
      // No existing filter on this field; add it.
      this.filters = [...this.filters, { field, value }];
      return;
    }

    if (isEmpty(value)) {
      // Filter has been cleared; remove it.
      this.filters = this.filters.filter((f) => f !== filter);
    } else {
      // Existing filter has been changed; update it.
      set(filter, 'value', value);
    }
  }

  @action
  nextStage(expulsion) {
    this.selectedExpulsion = expulsion;
    this.stage = expulsion.stage + 1;
  }

  @action
  notExpelled(expulsion) {
    this.selectedExpulsion = expulsion;
    this.stage = ExpulsionStage.NOT_EXPELLED;
  }

  @action
  closeDialog() {
    // Discard unsaved attribute and relationship edits (caregivers, the
    // nominated school contact) so they don't reappear when reopened.
    [
      this.selectedExpulsion,
      this.selectedExpulsionEdit,
      this.selectedExpulsionPosts,
      this.selectedExpulsionDelete,
    ].forEach((expulsion) => expulsion && rollbackRecord(expulsion));
    this.selectedExpulsion = null;
    this.selectedExpulsionEdit = null;
    this.selectedExpulsionPosts = null;
    this.selectedExpulsionDelete = null;

    // clean up any residual memory allocation
    this.countdownButton.cleanup();
  }

  @action
  openDeleteDialog(expulsion) {
    this.selectedExpulsionDelete = expulsion;
    this.countdownButton.initialise(3);
  }

  @action
  openEditDialog(expulsion) {
    this.selectedExpulsionEdit = expulsion;
  }

  @action
  openPostsDialog(expulsion, mode) {
    this.selectedExpulsionPosts = expulsion;
    this.postMode = mode;
  }

  @action
  highlightExpulsion(expulsion) {
    this.selectedTab = expulsion.stage;
    this.highlightedExpulsionId = expulsion.id;
  }

  @action
  expulsionTabChanged(newTabIndex) {
    this.selectedTab = newTabIndex;
    this.highlightedExpulsionId = '';
  }

  @action
  setExpulsionDate(prop, expulsion, date) {
    expulsion.set(prop, date);
    this.saveExpulsion(expulsion);
  }

  @action
  deleteExpulsion(expulsion) {
    return expulsion
      .get('incident')
      .then((incident) => {
        incident.set('behaviourIncident', null);
        incident.set('outcome', IncidentOutcome.UNRESOLVED);
        return incident.save();
      })
      .then(() => {
        return expulsion.destroyRecord();
      })
      .then(() => {
        this.toaster.success('Pending Expulsion successfully deleted');
      })
      .catch((e) => {
        this.toaster.error('Error deleting Expulsion: ' + e);
      })
      .finally(() => {
        this.closeDialog();
      });
  }

  @action
  saveExpulsion(expulsion) {
    expulsion
      .save()
      .then((savedExpulsion) => {
        this.highlightExpulsion(savedExpulsion);
      })
      .catch((error) => {
        this.toaster.error(error);
      });
  }
}
